import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, X, CheckCircle, Loader2, Calendar } from 'lucide-react';
import { dataRightsService, DataRightsStatus } from '../../services/compliance/dataRightsService';
import { Card } from '../ui/Card';
import { ModalOverlay } from '../ui/ModalOverlay';
import { differenceInDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Anonymization Section
 *
 * Allows users to request account anonymization with a 30-day cooling-off period.
 * Shows countdown and allows cancellation within the window.
 */
interface AnonymizationSectionProps {
  /** Defaults to athlete copy. Parent copy notes guardianship unlink on schedule. */
  persona?: 'athlete' | 'parent';
  /** Fired after parent schedule/cancel mutates guardianships so callers can refresh lists. */
  onGuardianshipChange?: () => void;
}

const AnonymizationSection: React.FC<AnonymizationSectionProps> = ({
  persona = 'athlete',
  onGuardianshipChange,
}) => {
  const { signOut } = useAuth();
  const isParent = persona === 'parent';
  const [status, setStatus] = useState<DataRightsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load status on mount and periodically
  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    const result = await dataRightsService.getStatus();
    if (result.success && result.data) {
      setStatus(result.data);
    }
    setLoading(false);
  };

  const handleRequestAnonymization = async () => {
    if (!password) {
      setMessage({ type: 'error', text: 'Please enter your password' });
      return;
    }

    if (!confirmCheckbox) {
      setMessage({ type: 'error', text: 'Please confirm you understand the consequences' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const result = await dataRightsService.requestAnonymization(password);
      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Account deletion request submitted. You have 30 days to cancel.',
        });
        setShowModal(false);
        setPassword('');
        setConfirmCheckbox(false);
        await loadStatus();
        if (isParent) {
          onGuardianshipChange?.();
        }
        await signOut();
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to submit request. Please try again.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!status?.pendingAnonymization) return;

    setCancelling(true);
    setMessage(null);

    try {
      const result = await dataRightsService.cancelAnonymization(
        status.pendingAnonymization.request_id
      );
      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Account deletion request cancelled successfully.',
        });
        await loadStatus();
        if (isParent) {
          onGuardianshipChange?.();
        }
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to cancel request. Please try again.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setCancelling(false);
    }
  };

  const pendingRequest = status?.pendingAnonymization;
  const daysRemaining = pendingRequest
    ? differenceInDays(new Date(pendingRequest.scheduled_at), new Date())
    : null;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-gray-600 mb-3">
          Under the CCPA, GDPR, UK GDPR, and other applicable privacy laws, you have the right to
          request deletion of your personal information.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
          <p className="font-semibold text-blue-900 mb-2">
            What happens when you delete your account:
          </p>
          <ul className="text-blue-800 space-y-1 list-disc list-inside ml-2">
            <li>Your personal information (name, email) will be permanently removed</li>
            <li>Your account will be deleted and you cannot log in again</li>
            {isParent ? (
              <>
                <li>
                  Active guardianship links end immediately — linked athletes become unguarded for
                  coaches until a parent consents again
                </li>
                <li>
                  If you cancel within 7 days, guardianship and roster consent are restored
                  automatically; after 7 days, a parent must consent again
                </li>
                <li>Prior parental consent records are retained for compliance</li>
              </>
            ) : (
              <>
                <li>
                  Your performance data (power, stroke rate, etc.) is retained for team analytics
                  with your name and other identifying details removed
                </li>
                <li>Your coach will no longer have access to your personal information</li>
              </>
            )}
            <li>You have 30 days to cancel this request</li>
          </ul>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div
          className={`p-4 rounded-lg flex items-start ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {/* Pending Request Card */}
      {loading ? (
        <Card className="border border-rowing-gray-200">
          <Card.Body className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto mb-4" />
            <p className="text-rowing-gray-600 font-medium">Loading...</p>
          </Card.Body>
        </Card>
      ) : pendingRequest ? (
        <Card className="border border-orange-200 bg-orange-50">
          <Card.Body className="p-6">
            <div className="flex items-start">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center mr-4 flex-shrink-0">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-rowing-navy mb-2 font-display">
                  Account Deletion Scheduled
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Your account will be deleted on{' '}
                  <strong>
                    {new Date(pendingRequest.scheduled_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </strong>
                  . You have{' '}
                  <strong className="text-orange-600">
                    {daysRemaining !== null && daysRemaining > 0
                      ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                      : 'less than 1 day'}{' '}
                    remaining
                  </strong>{' '}
                  to cancel this request.
                </p>
                {daysRemaining !== null && daysRemaining > 0 ? (
                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="px-4 py-2 bg-white text-accent border border-accent rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center font-medium"
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Cancel Request
                      </>
                    )}
                  </button>
                ) : (
                  <p className="text-sm text-red-600 font-medium">
                    The cancellation window has expired. Your account will be deleted soon.
                  </p>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border border-rowing-gray-200">
          <Card.Body className="p-6">
            <div className="flex items-start">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                <Shield className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-rowing-navy mb-2 font-display">
                  Delete My Account
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  This will permanently remove your personal information (name, email) while
                  preserving anonymized training data for team analytics. You will be logged out and
                  unable to access your account. This action cannot be undone after the 30-day
                  window expires.
                </p>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center font-medium"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Delete My Account
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Request Modal — portal overlays full viewport (sidebar + brand bar) */}
      <ModalOverlay
        isOpen={showModal}
        onClose={() => {
          if (submitting) return;
          setShowModal(false);
          setPassword('');
          setConfirmCheckbox(false);
          setMessage(null);
        }}
      >
        <Card className="max-w-md w-full border border-rowing-gray-200">
          <Card.Header className="px-6 py-4 border-b border-rowing-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-rowing-navy font-display">
                Confirm Account Deletion
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setPassword('');
                  setConfirmCheckbox(false);
                  setMessage(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-6 space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-semibold mb-2">Important: This action is irreversible</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Your personal information will be permanently deleted</li>
                    <li>You will be logged out after the 30-day period</li>
                    <li>You will not be able to access your account again</li>
                    {isParent ? (
                      <li>
                        Active guardianship links end when you schedule deletion (consent history is
                        kept)
                      </li>
                    ) : (
                      <li>Training data will be preserved (without your name)</li>
                    )}
                    <li>You have 30 days to cancel this request</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus-visible:ring-accent/50 focus:border-transparent"
                placeholder="Your password"
                autoComplete="current-password"
              />
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="confirm-anonymization"
                checked={confirmCheckbox}
                onChange={(e) => setConfirmCheckbox(e.target.checked)}
                className="mt-1 h-4 w-4 text-accent focus-visible:ring-accent/50 border-gray-300 rounded"
              />
              <label htmlFor="confirm-anonymization" className="ml-3 text-sm text-gray-700">
                I understand that this action cannot be undone after the 30-day window expires and I
                will lose access to my account.
              </label>
            </div>

            {message && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {message.text}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  setPassword('');
                  setConfirmCheckbox(false);
                  setMessage(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestAnonymization}
                disabled={submitting || !password || !confirmCheckbox}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </Card.Body>
        </Card>
      </ModalOverlay>
    </div>
  );
};

export default AnonymizationSection;
